import { z } from "zod";
/**
 * Engagement-scoped portal object grants.
 *
 * Staff publish or revoke individual objects into the customer portal; portal
 * read models use active grants as the source of truth for visibility.
 */
export const PortalObjectKindSchema = z.enum([
    "document_folder",
    "document",
    "workspace_document",
    "project",
    "retainer",
    "signature",
    "meeting",
    "approval",
    "invoice",
    "board_pack",
    "supplier_item",
    "subject",
]);
export const PortalObjectGrantAccessLevelSchema = z.enum(["read", "comment"]);
const QueryBooleanSchema = z.preprocess((value) => {
    if (value === undefined || typeof value === "boolean")
        return value;
    if (typeof value !== "string")
        return value;
    const normalized = value.trim().toLowerCase();
    if (normalized === "true" || normalized === "1")
        return true;
    if (normalized === "false" || normalized === "0")
        return false;
    return value;
}, z.boolean().default(false));
export const PortalObjectGrantSchema = z.object({
    id: z.string().uuid(),
    engagementId: z.string().uuid(),
    objectKind: PortalObjectKindSchema,
    objectId: z.string().uuid(),
    objectVersionId: z.string().uuid().nullable(),
    accessLevel: PortalObjectGrantAccessLevelSchema,
    title: z.string().max(300).nullable(),
    grantedBy: z.string().email(),
    grantedAt: z.string().datetime(),
    revokedAt: z.string().datetime().nullable(),
    revokedBy: z.string().email().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const PortalObjectGrantListQuerySchema = z.object({
    objectKind: PortalObjectKindSchema.optional(),
    includeRevoked: QueryBooleanSchema,
});
export const PortalObjectGrantCandidateQuerySchema = z.object({
    objectKind: PortalObjectKindSchema.optional(),
});
export const PortalObjectGrantCreateSchema = z.object({
    objectKind: PortalObjectKindSchema,
    objectId: z.string().uuid(),
    accessLevel: PortalObjectGrantAccessLevelSchema.default("read"),
    title: z.string().trim().min(1).max(300).optional(),
    // For workspace_document grants: pins the specific version that was current
    // at share time. The server stores this as objectVersionId on the grants row
    // so the portal viewer always sees the version that was shared, not the latest.
    objectVersionId: z.string().uuid().optional(),
});
/**
 * Share a whole Vault folder with a portal engagement in one action. The server
 * fans this out into one `document` grant per non-archived document currently in
 * the folder (a snapshot at share time — documents added to the folder later are
 * NOT auto-shared; live folder-sync is a deliberate follow-up). This reuses the
 * per-document grant / read / isolation / version / audit path unchanged — no
 * `document_folder` grant row is created — so the reviews viewer needs no change.
 */
export const PortalFolderShareCreateSchema = z.object({
    folderId: z.string().uuid(),
    accessLevel: PortalObjectGrantAccessLevelSchema.default("read"),
});
export const PortalFolderShareResultSchema = z.object({
    /** How many documents in the folder were shared (0 if the folder was empty). */
    sharedCount: z.number().int().min(0),
});
export const PortalObjectGrantCandidateSchema = z.object({
    objectKind: PortalObjectKindSchema,
    objectId: z.string().uuid(),
    title: z.string().min(1).max(300),
    description: z.string().max(500).nullable(),
    status: z.string().max(80).nullable(),
    grantable: z.boolean(),
    disabledReason: z.string().max(200).nullable(),
    updatedAt: z.string().datetime().nullable(),
});
export const PortalObjectGrantListResponseSchema = z.object({
    grants: z.array(PortalObjectGrantSchema),
});
export const PortalObjectGrantCandidateListResponseSchema = z.object({
    candidates: z.array(PortalObjectGrantCandidateSchema),
});
// ─── Portal shared-document read model ─────────────────────
/**
 * Projection returned by `GET /api/portal/shared-documents`.
 *
 * grantId is the portal_object_grants PK — used by the portal UI as
 * the stable handle to open the viewer and load the comment thread.
 *
 * stage is derived server-side: workspace_document grants → "working";
 * vault document grants → "final".
 *
 * Canon: docs/superpowers/plans/2026-06-07-portal-external-document-review.md Task 5.
 */
export const PortalSharedDocumentSchema = z.object({
    grantId: z.string(),
    objectKind: PortalObjectKindSchema,
    title: z.string(),
    accessLevel: PortalObjectGrantAccessLevelSchema,
    // Server-derived; loose z.string() per response-schema policy
    // (ADR canon: loosen response schemas, tighten request schemas).
    stage: z.string(),
    grantedAt: z.string(),
    unreadCount: z.number().int().nonnegative().default(0),
});
export const PortalSharedDocumentListResponseSchema = z.object({
    documents: z.array(PortalSharedDocumentSchema),
});
/**
 * Response from `GET /api/portal/shared-documents/:grantId/content`.
 * Returns a short-lived signed URL the browser follows directly to
 * blob storage — the API never streams bytes.
 */
export const PortalSharedDocumentContentResponseSchema = z.object({
    url: z.string(),
    filename: z.string(),
    expiresAt: z.string(),
});
/**
 * Counterparty return-file upload (Phase D): marked-up copy against a
 * workspace_document review grant → new working version for staff (not tip).
 */
export const PortalSharedDocumentReturnUploadRequestSchema = z.object({
    filename: z.string().trim().min(1).max(500),
    mimeType: z.string().trim().min(1).max(150),
    sizeBytes: z.coerce
        .number()
        .int()
        .min(1)
        .max(100 * 1024 * 1024),
    note: z.string().trim().max(2000).optional(),
});
export const PortalSharedDocumentReturnUploadTokenResponseSchema = z.object({
    uploadUrl: z.string().url(),
    blobKey: z.string().min(1),
    expiresAt: z.string().datetime(),
});
export const PortalSharedDocumentReturnCommitSchema = z.object({
    blobKey: z.string().min(1).max(1024),
    filename: z.string().trim().min(1).max(500),
    mimeType: z.string().trim().min(1).max(150),
    sizeBytes: z.coerce
        .number()
        .int()
        .min(1)
        .max(100 * 1024 * 1024),
    note: z.string().trim().max(2000).optional(),
});
export const PortalSharedDocumentReturnCommitResponseSchema = z.object({
    versionId: z.string().uuid(),
    version: z.number().int().positive(),
    documentId: z.string().uuid(),
});
//# sourceMappingURL=portal-object-grants.js.map